/**
 * v109.9.6 — Approval Log dashboard tile.
 * Uses GET /edit-access-requests/log?days=200. Master sees ALL; others see own.
 */
import { useCallback, useEffect, useState } from "react";
import api, { formatError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "./ui/dialog";
import { ScrollText, Check, X as IconX, Clock } from "lucide-react";
import { toast } from "sonner";
import { ChangesDiff } from "./EditAccessRequest";

const MODULE_LABEL = {
  product_issuance:   "Product Issuance",
  dispatch_orders:    "Dispatch & Logistics",
  vendor_management:  "Vendor Management",
  it_assets:          "IT Assets",
  it_tickets:         "Support Tickets",
  crm_clients:        "Client Database",
  documents:          "Documents",
  announcements:      "Announcements",
  incentive_config:   "Incentive",
  trainers:           "Trainers",
};

export default function ApprovalLogTile() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [counts, setCounts] = useState({ approved: 0, rejected: 0, pending: 0 });
  const [statusFilter, setStatusFilter] = useState("all");

  const isMaster = user?.role === "master_admin";
  const hasUser = !!user?.id;

  const loadCounts = useCallback(async () => {
    if (!hasUser) return;
    try {
      const { data } = await api.get("/edit-access-requests/log?days=200");
      const approved = (data || []).filter((r) => r.status === "approved").length;
      const rejected = (data || []).filter((r) => r.status === "rejected").length;
      const pending = (data || []).filter((r) => r.status === "pending").length;
      setCounts({ approved, rejected, pending });
    } catch { /* ignore */ }
  }, [hasUser]);

  const loadList = useCallback(async () => {
    if (!open) return;
    setLoading(true);
    try {
      const { data } = await api.get("/edit-access-requests/log?days=200");
      const filtered = statusFilter === "all" ? (data || []) : (data || []).filter((r) => r.status === statusFilter);
      setItems(filtered);
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
    } finally { setLoading(false); }
  }, [open, statusFilter]);

  useEffect(() => { loadCounts(); }, [loadCounts]);
  useEffect(() => { if (open) loadList(); }, [open, loadList]);

  if (!hasUser) return null;

  const totalCount = counts.approved + counts.rejected + counts.pending;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="pk-3d w-full text-left rounded-xl border border-slate-200 bg-gradient-to-br from-white to-slate-50 hover:from-violet-50 hover:to-white hover:border-violet-200 hover:shadow-md transition-all p-4 group"
        data-testid="approval-log-tile"
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2 mb-1.5">
              <ScrollText size={22} strokeWidth={2} className="text-violet-600 group-hover:scale-110 transition-transform" />
              <div className="font-bold text-slate-900 text-[13px]">Approval Log</div>
              {isMaster && <Badge className="bg-violet-100 text-violet-800 text-[9px]">Master · All Users</Badge>}
            </div>
            <div className="text-[10.5px] text-slate-500 mb-2">
              Last 200 days · click to view {isMaster ? "all approvals" : "your approval history"}
            </div>
            <div className="flex items-center gap-1.5 flex-wrap">
              <Badge className="bg-emerald-100 text-emerald-800 text-[10px]">
                <Check size={9} className="mr-1" /> {counts.approved} approved
              </Badge>
              <Badge className="bg-rose-100 text-rose-800 text-[10px]">
                <IconX size={9} className="mr-1" /> {counts.rejected} rejected
              </Badge>
              {counts.pending > 0 && (
                <Badge className="bg-amber-100 text-amber-800 text-[10px]">
                  <Clock size={9} className="mr-1" /> {counts.pending} pending
                </Badge>
              )}
            </div>
          </div>
          <div className="text-right shrink-0">
            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-400">Total</div>
            <div className="mono font-extrabold text-slate-800 text-2xl leading-none">{totalCount}</div>
          </div>
        </div>
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="approval-log-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ScrollText size={17} className="text-violet-600" />
              Approval Log
              <Badge className="bg-slate-100 text-slate-600 text-[10px]">Last 200 days</Badge>
              {isMaster && <Badge className="bg-violet-100 text-violet-800 text-[10px]">Master view · all users</Badge>}
            </DialogTitle>
          </DialogHeader>
          <div className="flex items-center gap-1.5 mb-3 flex-wrap">
            {["all", "approved", "rejected", "pending"].map((s) => (
              <Button
                key={s}
                variant={statusFilter === s ? "default" : "outline"}
                size="sm"
                onClick={() => setStatusFilter(s)}
                className="h-7 text-[11px] capitalize"
                data-testid={`approval-log-filter-${s}`}
              >
                {s}
              </Button>
            ))}
            <div className="ml-auto text-[11px] text-slate-500 mono">{items.length} entries</div>
          </div>
          {loading ? (
            <div className="text-center py-10 text-slate-400 text-sm">Loading…</div>
          ) : items.length === 0 ? (
            <div className="text-center py-10 text-slate-400 text-sm">No {statusFilter === "all" ? "" : statusFilter + " "}approvals in the last 200 days.</div>
          ) : (
            <div className="space-y-2" data-testid="approval-log-list">
              {items.map((r) => (
                <div key={r.id} className="rounded-md border border-slate-200 bg-white p-3" data-testid={`approval-log-row-${r.id}`}>
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <div className="min-w-0 flex-1">
                      <div className="text-[12.5px] font-bold text-slate-900">
                        {MODULE_LABEL[r.module_key] || r.module_key}
                        <span className="ml-1.5 mono text-[11px] text-violet-700">{r.record_label}</span>
                      </div>
                      <div className="text-[11px] text-slate-500 flex flex-wrap gap-x-2">
                        <span>by <b className="text-slate-700">{r.user_name}</b></span>
                        {r.assignee_name && <span>→ <b className="text-slate-700">{r.assignee_name}</b></span>}
                        {r.reviewed_by_name && r.reviewed_by_name !== r.assignee_name && <span>· reviewed by <b>{r.reviewed_by_name}</b></span>}
                        <span className="mono text-slate-400">{r.created_at ? new Date(r.created_at).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—"}</span>
                      </div>
                    </div>
                    <Badge className={`text-[10px] ${
                      r.status === "pending" ? "bg-amber-100 text-amber-800" :
                      r.status === "approved" ? "bg-emerald-100 text-emerald-800" :
                      "bg-rose-100 text-rose-800"
                    }`}>{r.status}</Badge>
                  </div>
                  <div className="text-[11px] text-slate-700 bg-slate-50 rounded px-2 py-1 mb-1">
                    <b>Reason:</b> {r.reason}
                  </div>
                  {r.changes && Object.keys(r.changes).length > 0 && (
                    <ChangesDiff changes={r.changes} />
                  )}
                  {r.review_reason && (
                    <div className="text-[11px] text-slate-500 italic mt-1">Reviewer note: “{r.review_reason}”</div>
                  )}
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
